import { useQuery } from '@tanstack/react-query';
import { decodeGlobalId } from '@ifixit/helpers';
import { _js } from '@ifixit/localize';
import { OrdersTable } from './orders_table';
import { OrderType, TrackingType } from './orders_types';
import type { ShopifyStorefront } from 'Shared/shopify/storefront';
import {
   getCustomerOrdersFromAllStores,
   getLocalizedFulfillmentStatus,
} from 'Shared/shopify_orders';
import type { ShopifyOrder } from 'Shared/shopify_orders';

type ServerSideData = {
   orders: OrderType[];
   shopifyStorefronts: ShopifyStorefront[] | null;
   storeName: string;
   isUS: boolean;
};

export function OrderHistory({ data }: { data: ServerSideData }) {
   const shouldFetchFromShopify =
      data.shopifyStorefronts !== null && data.shopifyStorefronts.length > 0;

   const ordersQuery = useQuery({
      queryKey: ['orders', data.shopifyStorefronts?.map(sf => sf.storeDomain)],
      queryFn: () =>
         data.shopifyStorefronts
            ? getCustomerOrdersFromAllStores(data.shopifyStorefronts).then(formatOrderData)
            : [],
      enabled: shouldFetchFromShopify,
      retry: false,
   });

   // Use Shopify data if available, otherwise use server-side data
   const orders: OrderType[] = shouldFetchFromShopify ? ordersQuery.data || [] : data.orders || [];

   return ordersQuery.isError ? (
      <ErrorMessage />
   ) : ordersQuery.isLoading && shouldFetchFromShopify ? (
      <LoadingMessage />
   ) : orders && orders.length > 0 ? (
      <OrdersDisplay orders={orders} />
   ) : (
      <NoOrdersMessage storeName={data.storeName} />
   );
}

function formatOrderData(orders: ShopifyOrder[]): OrderType[] {
   return orders.map((order: ShopifyOrder) => {
      const trackingInfo: TrackingType[] = [];
      order.successfulFulfillments.forEach(fulfillment => {
         fulfillment.trackingInfo.forEach(tracking => {
            trackingInfo.push({
               carrier: fulfillment.trackingCompany,
               number: tracking.number,
               link: tracking.url,
            });
         });
      });
      const [stepNumber, widthPercent] = getShopifyOrderStep(order);
      const thumbnail = order.lineItems?.nodes?.[0]?.variant?.image?.url;
      const item_count =
         order.lineItems?.nodes?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      return {
         orderid: order.orderNumber,
         currentStep: stepNumber,
         invoice_link: order.statusUrl,
         invoiceLink: order.statusUrl,
         reviewLink: orderReviewLink(order),
         order_name: order.name,
         status: getDetailedStatus(order),
         orderStatus: getDetailedStatus(order),
         widthPercent: widthPercent,
         editable: false,
         cancel_order_link: undefined,
         isReviewable: isSuccessfullyFulfilled(),
         init_date: Date.parse(order.processedAt) / 1000,
         total: order.totalPriceV2.amount,
         currency_type: order.totalPriceV2.currencyCode,
         shipment_tracking: trackingInfo,
         shopifyName: order.name,
         showTracking: order.successfulFulfillments.length ? true : false,
         thumbnail,
         item_count,
      };

      function isSuccessfullyFulfilled() {
         return (
            order.canceledAt == null &&
            order.fulfillmentStatus === 'FULFILLED' &&
            order.successfulFulfillments != null &&
            order.successfulFulfillments.length > 0 &&
            order.successfulFulfillments[0].trackingCompany != null
         );
      }
   });
}

const OrdersDisplay = ({
   orders,
}: {
   orders: OrderType[];
}) => (
   <div className="flex flex-col flex-1">
      <OrdersTable orders={orders} />
   </div>
);

const NoOrdersMessage = ({ storeName }: { storeName: string }) => (
   <p>{_js('You have no orders with iFixit %1', storeName)}</p>
);

const ErrorMessage = () => (
   <p>
      {_js("We're having trouble getting your orders. Please try again later or contact support")}
   </p>
);

const LoadingMessage = () => <p>{_js('Loading...')}</p>;

const step_2 = new Set([
   'UNFULFILLED',
   'RESTOCKED',
   'OPEN',
   'PENDING_FULFILLMENT',
   'PARTIALLY_FULFILLED',
   'IN_PROGRESS',
   'SCHEDULED',
]);
const step_3 = new Set(['FULFILLED']);

function getShopifyOrderStep(order: ShopifyOrder): number[] {
   if (step_2.has(order.fulfillmentStatus)) {
      return [2, 35];
   } else if (
      step_3.has(order.fulfillmentStatus) &&
      (order.successfulFulfillments?.length == 0 ||
         order.successfulFulfillments[0].trackingCompany == null)
   ) {
      return [3, 70];
   } else if (order.successfulFulfillments?.length > 0) {
      return [4, 100];
   }
   return [1, 0];
}

function getDetailedStatus(order: ShopifyOrder): string {
   if (
      step_3.has(order.fulfillmentStatus) &&
      (order.successfulFulfillments?.length == 0 ||
         order.successfulFulfillments[0].trackingCompany == null)
   ) {
      return _js('Packed');
   } else if (order.successfulFulfillments?.length > 0) {
      return _js('Shipped');
   }
   return getLocalizedFulfillmentStatus(order.fulfillmentStatus);
}

function orderReviewLink(order: ShopifyOrder) {
   const path = `/User/Reviews/order/${order.shopifyStorefront.nextLocale}/${decodeGlobalId(order.id)}`;
   const url = new URL(path, window.origin);
   const skus = order.lineItems.nodes
      .map(node => node.variant?.sku)
      .filter(Boolean)
      .join(',');
   const params = new URLSearchParams({ skus });
   return `${url.origin}${url.pathname}?${params}`;
}
